import React from 'react';

import { useGetMeFavoritesAsync } from '@/api/hooks/favoritesHooks';
import { FavoriteParsed } from '@/api/models/favoritesModels';

interface State {
  favorites: FavoriteParsed[] | undefined;
}

interface FetchFavoritesData {
  type: 'FETCH_FAVORITES_DATA';
  favorites: FavoriteParsed[] | undefined;
}

interface ClearFavoritesData {
  type: 'CLEAR_FAVORITES_DATA';
}

type Action = FetchFavoritesData | ClearFavoritesData;

type Dispatch = (action: Action) => void;

interface FavoritesContextData {
  state: State;
  dispatch: Dispatch;
  isFavsPending: boolean;
  isFavsError: boolean;
  fetchFavorites: () => void;
  clearFavorites: () => void;
}

const FavoritesContext = React.createContext<FavoritesContextData | undefined>(undefined);

function favoritesReducer(state: State, action: Action): State {
  switch (action.type) {
    case 'FETCH_FAVORITES_DATA':
      return {
        ...state,
        favorites: action.favorites
      };
    case 'CLEAR_FAVORITES_DATA':
      return {
        ...state,
        favorites: undefined
      };

    default:
      throw new Error(`Unhandled action type`);
  }
}

function FavoritesProvider({ children }: { children: React.ReactNode }) {
  const [state, dispatch] = React.useReducer(favoritesReducer, { favorites: undefined });

  const { mutateAsync: fetchFavs, isPending: isFavsPending, isError: isFavsError } = useGetMeFavoritesAsync();

  const fetchFavorites = () => {
    fetchFavs()
      .then((response) => {
        const parsedData: FavoriteParsed[] | undefined = response?.map((item) => ({
          favId: item.id,
          itemId:
            item.missions.at(0)?.id ||
            item.npos.at(0)?.id ||
            item.volunteers.at(0)?.id ||
            item.trendingPosts.at(0)?.id ||
            ''
        }));

        dispatch({
          type: 'FETCH_FAVORITES_DATA',
          favorites: parsedData
        });
      })
      .catch((e) => {
        console.error(e);
        dispatch({
          type: 'FETCH_FAVORITES_DATA',
          favorites: []
        });
      });
  };

  const clearFavorites = () => {
    dispatch({
      type: 'CLEAR_FAVORITES_DATA'
    });
  };

  return (
    <FavoritesContext.Provider value={{ state, dispatch, fetchFavorites, isFavsPending, isFavsError, clearFavorites }}>
      {children}
    </FavoritesContext.Provider>
  );
}

function useMeFavorites() {
  const context = React.useContext(FavoritesContext);

  if (context === undefined) {
    throw new Error('useMeFavorites must be used within a FavoritesProvider');
  }
  return context;
}

export { FavoritesProvider, useMeFavorites };
